import { getOtherUserData } from 'APIs/UserDataApi';
import { db } from 'app/firebase';
import { MessageDataType } from 'components/molecules/MessageBox';
import { addGeneralUser, addInboxUsersAuth } from 'features/auth/authSlice';
import { addInboxUsers, setCurrentIndex } from 'features/auth/chatSlice';
import {
  collection,
  doc,
  getDoc,
  getDocs,
  query,
  serverTimestamp,
  setDoc,
  Timestamp,
  updateDoc,
  where,
} from 'firebase/firestore';
import { IAuthState } from 'interfaces/auth.interfaces';
import { IChatState } from 'interfaces/chat.interfaces copy';
import { UserData } from 'interfaces/UserData.interface';
import { SetStateAction } from 'react';
import { NavigateFunction } from 'react-router-dom';
import { Dispatch } from 'redux';
import { v4 } from 'uuid';

export const getMessageDataType = (
  type: string
): MessageDataType | undefined => {
  switch (type) {
    case 'image':
      return MessageDataType.image;
    case 'video':
      return MessageDataType.video;
    case 'time':
      return MessageDataType.time;
    case 'tip':
      return MessageDataType.tip;
    case 'nft':
      return MessageDataType.nft;
    case 'invite':
      return MessageDataType.invite;
    case 'collab':
      return MessageDataType.collab;
    default:
      return undefined;
  }
};

export const fetchInboxUsers = async (
  authState: IAuthState,
  dispatch: Dispatch,
  setLoading: (arg: boolean) => SetStateAction<any>
) => {
  let docRef;
  const q = query(
    collection(db, 'chats'),
    where('users', 'array-contains', `${authState.username}`)
  );
  dispatch(setLoading(true));
  const querySnapshot = await getDocs(q);
  querySnapshot.forEach(async doc => {
    for (const user of doc.data().users) {
      if (user !== authState.username) {
        const userData = await getOtherUserData(user);
        try {
          if (authState.id && userData) {
            docRef = await getRef(authState.id, userData.id);
            const messages = await getMessages(authState.id, userData?.id);
            const messageChecker = messages.some(
              message => message.data().uid === authState.id
            );
            if (messageChecker || messages.length > 1) {
              // users.push(res.data);
              dispatch(
                addInboxUsers({
                  user: userData,
                  lastMessageTime:
                    messages[messages.length - 1].data().date.seconds,
                })
              );
              dispatch(addInboxUsersAuth(userData));
              if (
                !authState.generalChats.includes(userData.id) &&
                docRef &&
                !authState.subscribers?.some(
                  user => user.from.id === userData.id
                )
              ) {
                const q = query(
                  collection(db, 'chats', `${docRef}`, 'messages')
                );
                const messages = await getDocs(q);
                const userIdExists = messages.docs.some(
                  message => message.data().uid === authState.id
                );
                userIdExists && dispatch(addGeneralUser(userData.id));
              }
            }
          }
        } catch (error) {
          console.log(error);
        }
      }
    }
  });
  dispatch(setLoading(false));
};

export const getMessages = async (authId: number, userId: number) => {
  const q = query(collection(db, 'chats', `${authId}-${userId}`, 'messages'));
  const messages = await getDocs(q);
  if (messages.docs.length > 0) return messages.docs;
  else {
    const q = query(collection(db, 'chats', `${userId}-${authId}`, 'messages'));
    const messages = await getDocs(q);
    return messages.docs;
  }
};

export const onContact = async (
  authState: IAuthState,
  data: any,
  dispatch: Dispatch,
  navigate: NavigateFunction,
  mobile?: boolean
) => {
  if (data.id) {
    const index = authState.inboxUsers.findIndex(
      (el: UserData) => el.name === data.name
    );
    if (index === -1) {
      try {
        await setDoc(doc(db, 'chats', `${authState.id}-${data.id}`), {
          users: [data.username, `${authState.username}`],
        });
        await setDoc(
          doc(db, 'chats', `${authState.id}-${data.id}`, 'messages', v4()),
          {
            uid: authState.id,
            date: serverTimestamp(),
          }
        );
        dispatch(setCurrentIndex(authState.inboxUsers.length - 1));
        navigate(mobile ? '/message' : '/dmessage');
      } catch (error) {}
    } else {
      dispatch(setCurrentIndex(index));
      navigate(mobile ? '/message' : '/dmessage');
    }
  } else return;
};

//checks whether chat is authId-userId or userId-authId
export const getRef = async (authId: number, userId: number) => {
  const dbRef1 = doc(db, 'chats', `${authId}-${userId}`);
  const dbRef2 = doc(db, 'chats', `${userId}-${authId}`);
  try {
    const document1 = await getDoc(dbRef1);
    const document2 = await getDoc(dbRef2);
    if (document1.exists()) return `${authId}-${userId}`;
    else if (document2.exists()) return `${userId}-${authId}`;
    else return false;
  } catch (error) {
    console.log(error);
  }
};

export const handleSendMessage = async (
  msg: string,
  type:
    | 'image'
    | 'tip'
    | 'video'
    | 'nft'
    | 'invite'
    | undefined /* to be used */,
  authState: IAuthState,
  data: UserData,
  chatState: IChatState,
  currentIndex: number,
  isLocked?: boolean
) => {
  if (authState.id && data) {
    const dbRef = await getRef(authState.id, data.id);
    const updateFunction = async () => {
      try {
        const msgId = v4();
        await setDoc(doc(db, 'chats', `${dbRef}`, 'messages', msgId), {
          text: msg,
          uid: authState.id,
          date: Timestamp.now(),
          ...(type && { type: type }),
          ...(isLocked && { isLocked: isLocked, isPending: false }),
          ...(chatState.replyToMessage && {
            repliedTo: chatState.replyToMessage.msg,
            isRead: false,
            isSent: true,
          }),
          id: msgId,
        });
      } catch (error) {
        throw error;
      }
    };
    if (msg) {
      if (dbRef) await updateFunction();
      else {
        try {
          await setDoc(doc(db, 'chats', `${authState.id}-${data.id}`), {
            users: [
              authState.name,
              chatState.inboxUsers[currentIndex].user.username,
              `${authState.username}replied`,
            ],
          });
        } catch (error) {
          console.log(error);
        }
      }
    }
  }
};

export const handleLockMessages = async (
  authState: IAuthState,
  selectedMessagesIds: string[],
  setLoading: SetStateAction<any>,
  setSuccess: SetStateAction<any>,
  setError: SetStateAction<any>,
  setTransaction: SetStateAction<any>,
  setSubscribed: SetStateAction<any>,
  dispatch: Dispatch,
  isLocked: boolean,
  isPending: boolean,
  user?: UserData
) => {
  if (authState.id && user?.id) {
    setLoading(true);
    const dbRef = await getRef(authState.id, user.id);
    if (dbRef) {
      selectedMessagesIds.forEach(async messageId => {
        try {
          await updateDoc(doc(db, 'chats', dbRef, 'messages', messageId), {
            isLocked,
            isPending,
          });
          setLoading(false);
          setSuccess(true);
          setError(false);
        } catch (error) {
          console.log(error);
          setLoading(false);
          setError(true);
          setSuccess(false);
        }
      });
    } else {
      console.log('No chat found to update the relevant message.');
    }
  }
};
